import { useMutation, useQueryClient } from '@tanstack/react-query';
import { User, V2AdminUsersService } from 'src/api/generated';

export interface CreateUserData {
  email: string;
  profile: string;
  teamId: number | null;
  firstName?: string | null;
  lastName?: string | null;
  individualPhoneNumber?: string | null;
}

export interface UpdateUserData {
  firstName?: string;
  lastName?: string;
  email?: string;
  profile?: string;
  teamId?: number | null;
  individualPhoneNumber?: string | null;
}

interface UseCreateUserMutationResponse {
  createUser: (userData: CreateUserData) => Promise<User>;
  loading: boolean;
  error: Error | null;
}

export const useCreateUser = (
  onSuccess: (createdUser: User) => void = () => {},
  onError: (error: Error) => void = () => {}
): UseCreateUserMutationResponse => {
  const queryClient = useQueryClient();

  const mutate = async (userData: CreateUserData): Promise<User> => {
    return await V2AdminUsersService.createUser(userData);
  };

  const {
    mutateAsync,
    isPending: loading,
    error,
  } = useMutation({
    mutationFn: mutate,
    onSuccess: async (createdUser) => {
      await queryClient.invalidateQueries({ queryKey: ['admin/users'] });
      onSuccess(createdUser);
    },
    onError: (mutationError: Error) => onError(mutationError),
  });

  return {
    createUser: mutateAsync,
    loading,
    error,
  };
};

interface UseUpdateUserMutationResponse {
  updateUser: (params: {
    userId: number;
    userData: UpdateUserData;
  }) => Promise<User>;
  loading: boolean;
  error: Error | null;
}

export const useUpdateUser = (
  onSuccess: (updatedUser: User) => void = () => {},
  onError: (error: Error) => void = () => {}
): UseUpdateUserMutationResponse => {
  const queryClient = useQueryClient();

  const mutate = async (params: {
    userId: number;
    userData: UpdateUserData;
  }): Promise<User> => {
    return await V2AdminUsersService.updateUser(params.userId, params.userData);
  };

  const {
    mutateAsync,
    isPending: loading,
    error,
  } = useMutation({
    mutationFn: mutate,
    onSuccess: async (updatedUser, variables) => {
      await queryClient.invalidateQueries({ queryKey: ['admin/users'] });
      await queryClient.invalidateQueries({
        queryKey: ['admin/users', variables.userId],
      });
      await queryClient.invalidateQueries({
        queryKey: ['admin/users/current'],
      });
      onSuccess(updatedUser);
    },
    onError: (error: Error) => onError(error),
  });

  return { updateUser: mutateAsync, loading, error };
};

interface UseDeactivateUserMutationResponse {
  deactivateUser: (userId: number) => Promise<User>;
  loading: boolean;
  error: Error | null;
}

interface UseResendOnboardingMutationResponse {
  resendOnboarding: (userId: number) => Promise<User>;
  loading: boolean;
  error: Error | null;
}

export const useResendOnboarding = (
  onSuccess: (updatedUser: User) => void = () => {},
  onError: (error: Error) => void = () => {}
): UseResendOnboardingMutationResponse => {
  const queryClient = useQueryClient();

  const mutate = async (userId: number): Promise<User> => {
    return await V2AdminUsersService.resendOnboarding(userId);
  };

  const {
    mutateAsync,
    isPending: loading,
    error,
  } = useMutation({
    mutationFn: mutate,
    onSuccess: async (updatedUser, userId) => {
      await queryClient.invalidateQueries({ queryKey: ['admin/users'] });
      await queryClient.invalidateQueries({
        queryKey: ['admin/users', userId],
      });
      onSuccess(updatedUser);
    },
    onError: (mutationError: Error) => onError(mutationError),
  });

  return {
    resendOnboarding: mutateAsync,
    loading,
    error,
  };
};

interface UseCancelOnboardingMutationResponse {
  cancelOnboarding: (userId: number) => Promise<User>;
  loading: boolean;
  error: Error | null;
}

export const useCancelOnboarding = (
  onSuccess: (updatedUser: User) => void = () => {},
  onError: (error: Error) => void = () => {}
): UseCancelOnboardingMutationResponse => {
  const queryClient = useQueryClient();

  const mutate = async (userId: number): Promise<User> => {
    return await V2AdminUsersService.cancelOnboarding(userId);
  };

  const {
    mutateAsync,
    isPending: loading,
    error,
  } = useMutation({
    mutationFn: mutate,
    onSuccess: async (updatedUser, userId) => {
      await queryClient.invalidateQueries({ queryKey: ['admin/users'] });
      await queryClient.invalidateQueries({
        queryKey: ['admin/users', userId],
      });
      onSuccess(updatedUser);
    },
    onError: (mutationError: Error) => onError(mutationError),
  });

  return {
    cancelOnboarding: mutateAsync,
    loading,
    error,
  };
};

export const useDeactivateUser = (
  onSuccess: (updatedUser: User) => void = () => {},
  onError: (error: Error) => void = () => {}
): UseDeactivateUserMutationResponse => {
  const queryClient = useQueryClient();

  const mutate = async (userId: number): Promise<User> => {
    return await V2AdminUsersService.deactivateUser(userId);
  };

  const {
    mutateAsync,
    isPending: loading,
    error,
  } = useMutation({
    mutationFn: mutate,
    onSuccess: async (updatedUser, userId) => {
      await queryClient.invalidateQueries({ queryKey: ['admin/users'] });
      await queryClient.invalidateQueries({
        queryKey: ['admin/users', userId],
      });
      await queryClient.invalidateQueries({
        queryKey: ['admin/users/current'],
      });
      onSuccess(updatedUser);
    },
    onError: (error: Error) => onError(error),
  });

  return {
    deactivateUser: mutateAsync,
    loading,
    error,
  };
};

interface UseReactivateUserMutationResponse {
  reactivateUser: (userId: number) => Promise<User>;
  loading: boolean;
  error: Error | null;
}

export const useReactivateUser = (
  onSuccess: (updatedUser: User) => void = () => {},
  onError: (error: Error) => void = () => {}
): UseReactivateUserMutationResponse => {
  const queryClient = useQueryClient();

  const mutate = async (userId: number): Promise<User> => {
    return await V2AdminUsersService.reactivateUser(userId);
  };

  const {
    mutateAsync,
    isPending: loading,
    error,
  } = useMutation({
    mutationFn: mutate,
    onSuccess: async (updatedUser, userId) => {
      await queryClient.invalidateQueries({ queryKey: ['admin/users'] });
      await queryClient.invalidateQueries({
        queryKey: ['admin/users', userId],
      });
      await queryClient.invalidateQueries({
        queryKey: ['admin/users/current'],
      });
      onSuccess(updatedUser);
    },
    onError: (error: Error) => onError(error),
  });

  return {
    reactivateUser: mutateAsync,
    loading,
    error,
  };
};
